import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Heart, ArrowLeft, Users, TrendingUp, DollarSign, Sparkles,
  BarChart3, UserPlus, CreditCard, Activity, FileText, MessageSquare,
  Crown, Zap, RefreshCw, Download, Search, Mail, Tag, Plus, Trash2, Percent, Edit
} from 'lucide-react';
import { Progress } from '@/components/ui/progress';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

export default function AdminAnalytics({ onLogout }) {
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [planFilter, setPlanFilter] = useState('all');  // all, free, paid, expired
  
  // Discount codes state
  const [discountCodes, setDiscountCodes] = useState([]);
  const [loadingCodes, setLoadingCodes] = useState(false);
  const [showCreateCode, setShowCreateCode] = useState(false);
  const [editingCode, setEditingCode] = useState(null);
  const [newCode, setNewCode] = useState({
    code: '',
    discount_type: 'percentage',
    discount_value: '',
    max_uses: -1,
    expires_at: '',
    applicable_plans: ['basic', 'pro', 'clinical'],
    duration_months: 1
  });
  
  // State
  const [changingRole, setChangingRole] = useState(null);
  
  // Extend trial state (for manual plan adjustments)
  const [extendTrialDialog, setExtendTrialDialog] = useState(null);
  const [extendDays, setExtendDays] = useState(7);
  const [extendReason, setExtendReason] = useState('');
  const [extendingTrial, setExtendingTrial] = useState(false);

  useEffect(() => {
    fetchAnalytics();
    fetchUsers();
    fetchDiscountCodes();
  }, []);

  const fetchAnalytics = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/analytics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalytics(response.data);
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
      if (err.response?.status === 403) {
        setError('You do not have permission to view this page.');
      } else {
        setError('Failed to load analytics data.');
      }
      toast.error(err.response?.data?.detail || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    setLoadingUsers(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (err) {
      console.error('Failed to fetch users:', err);
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchDiscountCodes = async () => {
    setLoadingCodes(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/discount-codes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDiscountCodes(response.data);
    } catch (err) {
      console.error('Failed to fetch discount codes:', err);
    } finally {
      setLoadingCodes(false);
    }
  };

  const handleExtendTrial = async () => {
    if (!extendTrialDialog) return;
    
    setExtendingTrial(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/admin/users/${extendTrialDialog.id}/extend-trial`,
        { days: extendDays, reason: extendReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Trial extended by ${extendDays} days! New expiry: ${new Date(response.data.new_expiry).toLocaleDateString()}. Days remaining: ${response.data.days_remaining}`);
      setExtendTrialDialog(null);
      setExtendDays(7);
      setExtendReason('');
      fetchAnalytics(); // Refresh the data
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to extend trial');
    } finally {
      setExtendingTrial(false);
    }
  };

  const changeUserRole = async (userId, newRole) => {
    setChangingRole(userId);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/admin/users/${userId}/role`, 
        { role: newRole },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`User role changed to ${newRole}`);
      fetchUsers(); // Refresh user list
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change role');
    } finally {
      setChangingRole(null);
    }
  };

  const createDiscountCode = async () => {
    if (!newCode.code || !newCode.discount_value) {
      toast.error('Please fill in code and discount value');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/discount-codes`, {
        ...newCode,
        discount_value: parseFloat(newCode.discount_value),
        max_uses: newCode.max_uses === '' ? -1 : parseInt(newCode.max_uses),
        expires_at: newCode.expires_at || null,
        duration_months: parseInt(newCode.duration_months) || 1
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Discount code ${newCode.code.toUpperCase()} created!`);
      setShowCreateCode(false);
      setNewCode({
        code: '',
        discount_type: 'percentage',
        discount_value: '',
        max_uses: -1,
        expires_at: '',
        applicable_plans: ['basic', 'pro', 'clinical'],
        duration_months: 1
      });
      fetchDiscountCodes();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create discount code');
    }
  };

  const deleteDiscountCode = async (codeId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/discount-codes/${codeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Discount code deactivated');
      fetchDiscountCodes();
    } catch (err) {
      toast.error('Failed to delete discount code');
    }
  };

  const startEditCode = (code) => {
    setEditingCode({
      ...code,
      discount_value: String(code.discount_value),
      max_uses: code.max_uses,
      duration_months: code.duration_months || 1,
      expires_at: code.expires_at || ''
    });
  };

  const updateDiscountCode = async () => {
    if (!editingCode) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/admin/discount-codes/${editingCode.id}`, {
        discount_type: editingCode.discount_type,
        discount_value: parseFloat(editingCode.discount_value),
        max_uses: editingCode.max_uses === '' ? -1 : parseInt(editingCode.max_uses),
        expires_at: editingCode.expires_at || null,
        applicable_plans: editingCode.applicable_plans,
        duration_months: parseInt(editingCode.duration_months),
        is_active: editingCode.is_active
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Discount code updated');
      setEditingCode(null);
      fetchDiscountCodes();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update discount code');
    }
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = 
      user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = roleFilter === 'all' || user.role === roleFilter;
    
    // Plan filter
    const userPlan = user.plan || 'free';
    const isPaid = ['basic', 'pro', 'clinical'].includes(userPlan);
    const isFree = userPlan === 'free' || userPlan === 'free_trial' || !userPlan;
    
    let matchesPlan = true;
    if (planFilter === 'free') {
      matchesPlan = isFree;
    } else if (planFilter === 'paid') {
      matchesPlan = isPaid;
    }
    
    return matchesSearch && matchesRole && matchesPlan;
  });

  const exportToCSV = () => {
    const headers = ['Name', 'Email', 'Role', 'Plan', 'Status', 'Signed Up'];
    const rows = filteredUsers.map(user => [
      user.name || '',
      user.email || '',
      user.role || '',
      user.plan || 'free_trial',
      user.subscription_status || 'trial',
      user.created_at ? new Date(user.created_at).toLocaleDateString() : ''
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `playfirst_users_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    toast.success('User list exported!');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-stone-600">Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <Card className="p-8 max-w-md text-center">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
            <Crown className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-xl font-semibold text-stone-800 mb-2">Access Denied</h2>
          <p className="text-stone-600 mb-4">{error}</p>
          <Button onClick={() => navigate('/')} className="rounded-full">
            Go Back Home
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <nav className="bg-white border-b border-stone-100 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              data-testid="back-btn"
              onClick={() => navigate('/')}
              className="rounded-full"
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Heart className="w-5 h-5 text-primary" />
              </div>
              <h1 className="text-2xl font-serif text-primary-900">PlayFirst</h1>
            </div>
          </div>
          <Button
            variant="outline"
            onClick={fetchAnalytics}
            className="rounded-full"
            data-testid="refresh-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                <Crown className="w-5 h-5 text-purple-600" />
              </div>
              <h2 className="text-3xl font-serif text-primary-900">Admin Analytics</h2>
            </div>
            <p className="text-stone-600">
              Platform overview • Updated {new Date(analytics?.generated_at).toLocaleString()}
            </p>
          </div>

          {/* Key Metrics Row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <Card className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl border-0">
              <div className="flex items-center justify-between mb-2">
                <Users className="w-5 h-5 text-blue-600" />
                <span className="text-xs text-blue-600 font-medium">Total</span>
              </div>
              <p className="text-3xl font-bold text-blue-900">{analytics?.users?.total || 0}</p>
              <p className="text-sm text-blue-700">Users</p>
            </Card>
            
            <Card className="p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-2xl border-0">
              <div className="flex items-center justify-between mb-2">
                <CreditCard className="w-5 h-5 text-green-600" />
                <span className="text-xs text-green-600 font-medium">Paid</span>
              </div>
              <p className="text-3xl font-bold text-green-900">{analytics?.subscriptions?.paid || 0}</p>
              <p className="text-sm text-green-700">Subscribers</p>
            </Card>
            
            <Card className="p-4 bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl border-0">
              <div className="flex items-center justify-between mb-2">
                <TrendingUp className="w-5 h-5 text-purple-600" />
                <span className="text-xs text-purple-600 font-medium">Rate</span>
              </div>
              <p className="text-3xl font-bold text-purple-900">{analytics?.subscriptions?.conversion_rate || 0}%</p>
              <p className="text-sm text-purple-700">Conversion</p>
            </Card>
            
            <Card className="p-4 bg-gradient-to-br from-amber-50 to-amber-100 rounded-2xl border-0">
              <div className="flex items-center justify-between mb-2">
                <DollarSign className="w-5 h-5 text-amber-600" />
                <span className="text-xs text-amber-600 font-medium">Est.</span>
              </div>
              <p className="text-3xl font-bold text-amber-900">${analytics?.subscriptions?.estimated_mrr_usd || 0}</p>
              <p className="text-sm text-amber-700">Monthly Revenue</p>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {/* User Acquisition */}
            <Card className="p-6 bg-white rounded-3xl" data-testid="user-acquisition-card">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                  <UserPlus className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-primary-900">User Acquisition</h3>
                  <p className="text-sm text-stone-500">Signups and growth</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-stone-50 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-primary-900">{analytics?.users?.therapists || 0}</p>
                  <p className="text-xs text-stone-500">Therapists</p>
                </div>
                <div className="bg-stone-50 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-primary-900">{analytics?.users?.parents || 0}</p>
                  <p className="text-xs text-stone-500">Parents</p>
                </div>
              </div>

              <div className="mb-4">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-stone-600">New signups (30 days)</span>
                  <span className="font-medium">{analytics?.users?.recent_signups_30d || 0}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-stone-600">New therapists (30 days)</span>
                  <span className="font-medium">{analytics?.users?.recent_therapists_30d || 0}</span>
                </div>
              </div>

              {/* Daily Signups Chart */}
              <div className="mt-4">
                <p className="text-sm font-medium text-stone-700 mb-2">Daily Signups (14 days)</p>
                <div className="flex items-end gap-1 h-16">
                  {analytics?.users?.daily_signups?.map((day, i) => {
                    const maxVal = Math.max(...analytics.users.daily_signups.map(d => d.signups), 1);
                    const height = (day.signups / maxVal) * 100;
                    return (
                      <div key={i} className="flex-1 flex flex-col items-center">
                        <div 
                          className="w-full bg-blue-400 rounded-t"
                          style={{ height: `${Math.max(height, 4)}%` }}
                          title={`${day.date}: ${day.signups} signups`}
                        />
                      </div>
                    );
                  })}
                </div>
                <div className="flex justify-between text-xs text-stone-400 mt-1">
                  <span>{analytics?.users?.daily_signups?.[0]?.date}</span>
                  <span>{analytics?.users?.daily_signups?.[analytics?.users?.daily_signups?.length - 1]?.date}</span>
                </div>
              </div>
            </Card>

            {/* Subscription Breakdown */}
            <Card className="p-6 bg-white rounded-3xl" data-testid="subscription-card">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                  <CreditCard className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-primary-900">Subscriptions</h3>
                  <p className="text-sm text-stone-500">Plan distribution</p>
                </div>
              </div>

              <div className="space-y-4 mb-6">
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-stone-600">Free Plan</span>
                    <span className="font-medium">{analytics?.users?.by_plan?.free || 0}</span>
                  </div>
                  <Progress 
                    value={analytics?.users?.therapists > 0 
                      ? ((analytics.users.by_plan.free || 0) / analytics.users.therapists) * 100 
                      : 0} 
                    className="h-2 bg-stone-100 [&>div]:bg-stone-400" 
                  />
                </div>
                
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-stone-600">Basic ($9.99/mo)</span>
                    <span className="font-medium text-stone-700">{analytics?.users?.by_plan?.basic || 0}</span>
                  </div>
                  <Progress 
                    value={analytics?.users?.therapists > 0 
                      ? ((analytics.users.by_plan.basic || 0) / analytics.users.therapists) * 100 
                      : 0} 
                    className="h-2" 
                  />
                </div>
                
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-stone-600">Professional ($14.99/mo)</span>
                    <span className="font-medium text-blue-600">{analytics?.users?.by_plan?.pro || 0}</span>
                  </div>
                  <Progress 
                    value={analytics?.users?.therapists > 0 
                      ? ((analytics.users.by_plan.pro || 0) / analytics.users.therapists) * 100 
                      : 0} 
                    className="h-2 bg-blue-100 [&>div]:bg-blue-500" 
                  />
                </div>
                
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-stone-600">Clinical ($39.99/mo)</span>
                    <span className="font-medium text-purple-600">{analytics?.users?.by_plan?.clinical || 0}</span>
                  </div>
                  <Progress 
                    value={analytics?.users?.therapists > 0 
                      ? ((analytics.users.by_plan.clinical || 0) / analytics.users.therapists) * 100 
                      : 0} 
                    className="h-2 bg-purple-100 [&>div]:bg-purple-500" 
                  />
                </div>
              </div>

              <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-green-700">Paid Subscribers</p>
                    <p className="text-2xl font-bold text-green-800">
                      {(analytics?.users?.by_plan?.basic || 0) + (analytics?.users?.by_plan?.pro || 0) + (analytics?.users?.by_plan?.clinical || 0)}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-green-700">Free Users</p>
                    <p className="text-2xl font-bold text-green-800">{analytics?.users?.by_plan?.free || 0}</p>
                  </div>
                </div>
              </div>
            </Card>
          </div>

          {/* AI Usage Section */}
          <Card className="p-6 bg-white rounded-3xl mb-8" data-testid="ai-usage-card">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-primary-900">Platform AI Usage</h3>
                <p className="text-sm text-stone-500">All users combined</p>
              </div>
            </div>

            {/* Real-time Credit Consumption Alert */}
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl p-4 mb-6 border border-amber-200" data-testid="credit-consumption">
              <div className="flex items-center gap-2 mb-3">
                <Zap className="w-5 h-5 text-amber-600" />
                <h4 className="font-semibold text-amber-900">Real-time Credit Consumption</h4>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-white rounded-xl p-3 text-center">
                  <p className="text-xs text-stone-500 mb-1">Today</p>
                  <p className="text-xl font-bold text-amber-700">${analytics?.ai_usage?.today_cost?.cost || 0}</p>
                  <p className="text-xs text-stone-400">{analytics?.ai_usage?.today_cost?.count || 0} generations</p>
                </div>
                <div className="bg-white rounded-xl p-3 text-center">
                  <p className="text-xs text-stone-500 mb-1">This Week</p>
                  <p className="text-xl font-bold text-orange-700">${analytics?.ai_usage?.this_week_cost?.cost || 0}</p>
                  <p className="text-xs text-stone-400">{analytics?.ai_usage?.this_week_cost?.count || 0} generations</p>
                </div>
                <div className="bg-white rounded-xl p-3 text-center">
                  <p className="text-xs text-stone-500 mb-1">This Month</p>
                  <p className="text-xl font-bold text-red-700">${analytics?.ai_usage?.this_month_cost?.cost || 0}</p>
                  <p className="text-xs text-stone-400">{analytics?.ai_usage?.this_month_cost?.count || 0} generations</p>
                </div>
              </div>
              <p className="text-xs text-amber-700 mt-3 text-center">
                💡 Monitor this to ensure your Universal Key has sufficient balance
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-purple-50 rounded-xl p-4 text-center">
                <Sparkles className="w-6 h-6 mx-auto mb-2 text-purple-600" />
                <p className="text-2xl font-bold text-purple-900">{analytics?.ai_usage?.total_activities_generated || 0}</p>
                <p className="text-xs text-purple-600">Activities Generated</p>
              </div>
              <div className="bg-blue-50 rounded-xl p-4 text-center">
                <FileText className="w-6 h-6 mx-auto mb-2 text-blue-600" />
                <p className="text-2xl font-bold text-blue-900">{analytics?.ai_usage?.total_reports_generated || 0}</p>
                <p className="text-xs text-blue-600">Reports Generated</p>
              </div>
              <div className="bg-stone-50 rounded-xl p-4 text-center">
                <Activity className="w-6 h-6 mx-auto mb-2 text-stone-600" />
                <p className="text-2xl font-bold text-stone-900">{(analytics?.ai_usage?.total_tokens || 0).toLocaleString()}</p>
                <p className="text-xs text-stone-600">Total Tokens</p>
              </div>
              <div className="bg-green-50 rounded-xl p-4 text-center">
                <DollarSign className="w-6 h-6 mx-auto mb-2 text-green-600" />
                <p className="text-2xl font-bold text-green-900">${analytics?.ai_usage?.total_cost_usd || 0}</p>
                <p className="text-xs text-green-600">Total AI Cost (All Time)</p>
              </div>
            </div>

            {/* Monthly AI Usage Chart */}
            <div>
              <p className="text-sm font-medium text-stone-700 mb-3">Monthly AI Usage (6 months)</p>
              <div className="flex items-end gap-2 h-32">
                {analytics?.ai_usage?.monthly_breakdown?.map((month, i) => {
                  const total = month.activities + month.reports;
                  const maxVal = Math.max(...analytics.ai_usage.monthly_breakdown.map(m => m.activities + m.reports), 1);
                  const height = (total / maxVal) * 100;
                  return (
                    <div key={i} className="flex-1 flex flex-col items-center gap-1">
                      <div className="w-full flex flex-col justify-end" style={{ height: '100%' }}>
                        <div 
                          className="w-full bg-gradient-to-t from-purple-500 to-purple-400 rounded-t transition-all"
                          style={{ height: `${Math.max(height, 4)}%` }}
                          title={`${month.month}: ${month.activities} activities, ${month.reports} reports, $${month.cost}`}
                        />
                      </div>
                      <span className="text-xs text-stone-500">{month.month.split(' ')[0]}</span>
                      <span className="text-xs text-green-600">${month.cost}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Platform Engagement */}
            <Card className="p-6 bg-white rounded-3xl" data-testid="engagement-card">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                  <Activity className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-primary-900">Platform Engagement</h3>
                  <p className="text-sm text-stone-500">Content created</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-stone-50 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-primary-900">{analytics?.engagement?.active_therapists_7d || 0}</p>
                  <p className="text-xs text-stone-500">Active (7 days)</p>
                </div>
                <div className="bg-stone-50 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-primary-900">{analytics?.engagement?.total_children || 0}</p>
                  <p className="text-xs text-stone-500">Children Profiles</p>
                </div>
                <div className="bg-stone-50 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-primary-900">{analytics?.engagement?.total_activities || 0}</p>
                  <p className="text-xs text-stone-500">Activities</p>
                </div>
                <div className="bg-stone-50 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-primary-900">{analytics?.engagement?.total_chunks || 0}</p>
                  <p className="text-xs text-stone-500">Language Chunks</p>
                </div>
              </div>
            </Card>

            {/* Top AI Users */}
            <Card className="p-6 bg-white rounded-3xl" data-testid="top-users-card">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                  <Zap className="w-5 h-5 text-indigo-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-primary-900">Top AI Users</h3>
                  <p className="text-sm text-stone-500">Most active generators</p>
                </div>
              </div>

              <div className="space-y-3">
                {analytics?.top_ai_users?.length > 0 ? (
                  analytics.top_ai_users.map((user, i) => (
                    <div key={i} className="flex items-center justify-between p-3 bg-stone-50 rounded-xl">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm
                          ${i === 0 ? 'bg-yellow-500' : i === 1 ? 'bg-stone-400' : i === 2 ? 'bg-amber-600' : 'bg-stone-300'}`}>
                          {i + 1}
                        </div>
                        <div>
                          <p className="font-medium text-stone-800">{user.name}</p>
                          <p className="text-xs text-stone-500">{user.plan} plan</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-primary-900">{user.total_generations}</p>
                        <p className="text-xs text-green-600">${user.total_cost?.toFixed(2)}</p>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-stone-500 text-center py-4">No AI usage yet</p>
                )}
              </div>
            </Card>
          </div>

          {/* User Directory */}
          <Card className="p-6 bg-white rounded-3xl mt-8" data-testid="user-directory">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-primary-900">User Directory</h3>
                  <p className="text-sm text-stone-500">
                    {filteredUsers.length} of {users.length} total users
                  </p>
                </div>
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                className="rounded-full"
                onClick={exportToCSV}
                data-testid="export-csv-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
            </div>

            {/* Filters */}
            <div className="flex flex-col gap-3 mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-stone-400" />
                <Input
                  type="text"
                  placeholder="Search by name or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 rounded-full"
                  data-testid="user-search"
                />
              </div>
              <div className="flex flex-wrap gap-2">
                <span className="text-sm text-stone-500 py-1">Role:</span>
                <Button
                  variant={roleFilter === 'all' ? 'default' : 'outline'}
                  size="sm"
                  className="rounded-full h-8"
                  onClick={() => setRoleFilter('all')}
                >
                  All
                </Button>
                <Button
                  variant={roleFilter === 'therapist' ? 'default' : 'outline'}
                  size="sm"
                  className="rounded-full h-8"
                  onClick={() => setRoleFilter('therapist')}
                >
                  Therapists
                </Button>
                <Button
                  variant={roleFilter === 'parent' ? 'default' : 'outline'}
                  size="sm"
                  className="rounded-full h-8"
                  onClick={() => setRoleFilter('parent')}
                >
                  Parents
                </Button>
                
                <span className="text-sm text-stone-500 py-1 ml-4">Plan:</span>
                <Button
                  variant={planFilter === 'all' ? 'default' : 'outline'}
                  size="sm"
                  className="rounded-full h-8"
                  onClick={() => setPlanFilter('all')}
                >
                  All Plans
                </Button>
                <Button
                  variant={planFilter === 'free' ? 'default' : 'outline'}
                  size="sm"
                  className="rounded-full h-8"
                  onClick={() => setPlanFilter('free')}
                >
                  Free
                </Button>
                <Button
                  variant={planFilter === 'paid' ? 'default' : 'outline'}
                  size="sm"
                  className="rounded-full h-8"
                  onClick={() => setPlanFilter('paid')}
                >
                  Paid
                </Button>
              </div>
            </div>

            {/* User Table */}
            {loadingUsers ? (
              <div className="text-center py-8">
                <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2 text-stone-400" />
                <p className="text-stone-500">Loading users...</p>
              </div>
            ) : filteredUsers.length === 0 ? (
              <div className="text-center py-8 bg-stone-50 rounded-2xl">
                <Users className="w-10 h-10 text-stone-300 mx-auto mb-2" />
                <p className="text-stone-500">No users found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-stone-200">
                      <th className="text-left py-3 px-4 text-sm font-medium text-stone-600">Name</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-stone-600">Email</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-stone-600">Role</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-stone-600">Plan</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-stone-600">Signed Up</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-stone-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.slice(0, 50).map((user, i) => {
                      const userPlan = user.plan || 'free';
                      
                      // Plan badge styles
                      const planStyles = {
                        'clinical': 'bg-purple-100 text-purple-800',
                        'pro': 'bg-blue-100 text-blue-800',
                        'basic': 'bg-green-100 text-green-800',
                        'free': 'bg-stone-100 text-stone-800',
                        'free_trial': 'bg-amber-100 text-amber-800',
                        'unlimited': 'bg-purple-100 text-purple-800',
                        'super_admin': 'bg-purple-100 text-purple-800'
                      };
                      
                      const planLabels = {
                        'clinical': 'Clinical',
                        'pro': 'Professional',
                        'basic': 'Basic',
                        'free': 'Free',
                        'free_trial': 'Free Trial',
                        'unlimited': 'Unlimited',
                        'super_admin': 'Super Admin'
                      };
                      
                      return (
                      <tr key={user.id || i} className="border-b border-stone-100 hover:bg-stone-50">
                        <td className="py-3 px-4">
                          <p className="font-medium text-stone-800">{user.name || 'No name'}</p>
                        </td>
                        <td className="py-3 px-4">
                          <p className="text-stone-600 text-sm">{user.email}</p>
                        </td>
                        <td className="py-3 px-4">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium
                            ${user.role === 'therapist' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'}`}>
                            {user.role}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${planStyles[userPlan] || planStyles.free}`}>
                            {planLabels[userPlan] || userPlan}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-sm text-stone-500">
                          {user.created_at ? new Date(user.created_at).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          }) : '-'}
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-1">
                            {/* Role change dropdown */}
                            {!user.is_super_admin && (
                              <Select
                                value={user.role}
                                onValueChange={(newRole) => changeUserRole(user.id, newRole)}
                                disabled={changingRole === user.id}
                              >
                                <SelectTrigger className="h-7 w-36 text-xs" data-testid={`role-select-${user.id}`}>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="therapist">Therapist</SelectItem>
                                  <SelectItem value="parent">Parent (invited by SLP)</SelectItem>
                                  <SelectItem value="parent_self">PlayFirst Parent (self)</SelectItem>
                                </SelectContent>
                              </Select>
                            )}
                            {user.is_super_admin && (
                              <span className="text-xs text-purple-600 font-medium">Super Admin</span>
                            )}
                          </div>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
                {filteredUsers.length > 50 && (
                  <p className="text-center text-sm text-stone-500 mt-4">
                    Showing 50 of {filteredUsers.length} users. Export CSV for full list.
                  </p>
                )}
              </div>
            )}
          </Card>
        </motion.div>
      </main>

      {/* Extend Trial Dialog */}
      <Dialog open={!!extendTrialDialog} onOpenChange={(open) => !open && setExtendTrialDialog(null)}>
        <DialogContent className="rounded-3xl max-w-md" data-testid="extend-trial-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-purple-600" />
              Extend Trial Period
            </DialogTitle>
          </DialogHeader>
          
          {extendTrialDialog && (
            <div className="space-y-4 mt-4">
              <div className="p-3 bg-stone-50 rounded-xl">
                <p className="font-medium text-stone-900">{extendTrialDialog.name}</p>
                <p className="text-sm text-stone-500">{extendTrialDialog.email}</p>
                <div className="flex items-center gap-2 mt-2 text-sm">
                  <span className="text-stone-600">Current status:</span>
                  {extendTrialDialog.expired ? (
                    <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-xs">Expired</span>
                  ) : (
                    <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs">
                      {extendTrialDialog.days_remaining} days left
                    </span>
                  )}
                  {extendTrialDialog.trial_extended && (
                    <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-xs">
                      Already extended +{extendTrialDialog.extension_days}d
                    </span>
                  )}
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="extend-days">Days to extend</Label>
                <Select value={String(extendDays)} onValueChange={(v) => setExtendDays(Number(v))}>
                  <SelectTrigger className="rounded-xl" data-testid="extend-days-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="3">3 days</SelectItem>
                    <SelectItem value="7">7 days (1 week)</SelectItem>
                    <SelectItem value="14">14 days (2 weeks)</SelectItem>
                    <SelectItem value="30">30 days (1 month)</SelectItem>
                    <SelectItem value="60">60 days (2 months)</SelectItem>
                    <SelectItem value="90">90 days (3 months)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="extend-reason">Reason (optional)</Label>
                <Input
                  id="extend-reason"
                  placeholder="e.g., User requested more time, technical issue, etc."
                  value={extendReason}
                  onChange={(e) => setExtendReason(e.target.value)}
                  className="rounded-xl"
                  data-testid="extend-reason-input"
                />
              </div>
              
              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  className="flex-1 rounded-full"
                  onClick={() => setExtendTrialDialog(null)}
                  disabled={extendingTrial}
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1 rounded-full bg-purple-600 hover:bg-purple-700"
                  onClick={handleExtendTrial}
                  disabled={extendingTrial}
                  data-testid="confirm-extend-btn"
                >
                  {extendingTrial ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Extending...
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4 mr-2" />
                      Extend by {extendDays} days
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
